import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Target, Calendar, CheckCircle, Clock, TrendingUp, 
  Plus, ChevronRight, Flame, Trophy, ArrowRight
} from "lucide-react";
import { API_BASE_URL } from "../lib/config";

const API_URL = API_BASE_URL;

const StudyPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState({
    category: "",
    template: "pass_30_days"
  });

  const categories = [
    { id: "staar_3", name: "STAAR Grade 3" },
    { id: "staar_4", name: "STAAR Grade 4" },
    { id: "staar_5", name: "STAAR Grade 5" },
    { id: "staar_6", name: "STAAR Grade 6" },
    { id: "staar_7", name: "STAAR Grade 7" },
    { id: "staar_8", name: "STAAR Grade 8" },
    { id: "staar_eoc", name: "STAAR EOC" },
    { id: "sat", name: "SAT" },
    { id: "act", name: "ACT" },
    { id: "asvab", name: "ASVAB" }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const [plansRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/study-plans`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/study-plans/templates`, { headers: getAuthHeaders() })
      ]);

      if (plansRes.ok) setPlans(await plansRes.json());
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async () => {
    if (!newPlan.category) {
      alert("Please select a test category");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/study-plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify(newPlan)
      });

      if (res.ok) {
        const data = await res.json();
        setShowCreate(false);
        fetchData();
      }
    } catch (error) {
      console.error("Error creating plan:", error);
    } finally {
      setCreating(false);
    }
  };

  const getProgressPercent = (plan) => {
    const start = new Date(plan.start_date);
    const target = new Date(plan.target_date);
    const now = new Date();
    const total = target - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const getDaysRemaining = (plan) => {
    const target = new Date(plan.target_date);
    const now = new Date();
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const activePlans = plans.filter(p => p.status === "active");
  const completedPlans = plans.filter(p => p.status === "completed");

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-lime-500 to-green-600 rounded-3xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl">Study Plans</h1>
            <p className="text-lime-100 mt-2">Set goals and track your progress</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-white/20 text-white font-medium rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Plan
          </button>
        </div>
      </div>

      {/* Active Plans */}
      {activePlans.length > 0 && (
        <section>
          <h2 className="font-heading font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Active Plans
          </h2>
          <div className="grid gap-4">
            {activePlans.map(plan => (
              <Link
                key={plan.id}
                to={`/study-plans/${plan.id}`}
                className="bg-white rounded-2xl border-2 border-slate-100 p-6 hover:border-lime-300 transition-colors group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-slate-800">{plan.category_name}</h3>
                    <p className="text-slate-500 text-sm">{plan.template_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-2xl text-lime-600">
                      {Math.round(plan.progress?.current_mastery * 100 || 0)}%
                    </p>
                    <p className="text-slate-400 text-xs">Mastery</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Progress</span>
                    <span className="font-medium text-slate-700">{getDaysRemaining(plan)} days left</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-lime-400 to-green-500 rounded-full"
                      style={{ width: `${getProgressPercent(plan)}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Target className="w-4 h-4" />
                      {plan.daily_questions}/day
                    </div>
                    <div className="flex items-center gap-1 text-slate-500">
                      <CheckCircle className="w-4 h-4" />
                      {plan.progress?.days_completed || 0} days done
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-lime-500 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {activePlans.length === 0 && (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 text-center">
          <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-lime-600" />
          </div>
          <h3 className="font-heading font-bold text-xl text-slate-800 mb-2">No Active Plans</h3>
          <p className="text-slate-500 mb-4">Create a study plan to stay on track and reach your goals!</p>
          <button
            onClick={() => setShowCreate(true)}
            className="px-6 py-3 bg-lime-500 text-white font-bold rounded-xl hover:bg-lime-600 transition-colors"
          >
            Create Your First Plan
          </button>
        </div>
      )}

      {/* Completed Plans */}
      {completedPlans.length > 0 && (
        <section>
          <h2 className="font-heading font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Completed Plans
          </h2>
          <div className="grid gap-3">
            {completedPlans.map(plan => (
              <div key={plan.id} className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-800">{plan.category_name}</h3>
                  <p className="text-sm text-slate-500">{plan.template_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {Math.round(plan.progress?.current_mastery * 100 || 0)}% Mastery
                  </span>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Create Plan Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-lime-500 to-green-600 text-white p-6">
              <h2 className="font-heading font-bold text-xl">Create Study Plan</h2>
              <p className="text-lime-100 text-sm">Set a goal and commit to your success</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Test</label>
                <select
                  value={newPlan.category}
                  onChange={(e) => setNewPlan({...newPlan, category: e.target.value})}
                  className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-lime-500 focus:ring-0"
                >
                  <option value="">Choose a test...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Study Plan Type</label>
                <div className="space-y-2">
                  {templates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => setNewPlan({...newPlan, template: template.id})}
                      className={`w-full p-4 rounded-xl text-left transition-colors ${
                        newPlan.template === template.id
                          ? "bg-lime-100 border-2 border-lime-400"
                          : "bg-slate-50 border-2 border-slate-200 hover:border-lime-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-slate-800">{template.name}</p>
                          <p className="text-sm text-slate-500">
                            {template.daily_questions} questions/day • {template.duration_days} days
                          </p>
                        </div>
                        <span className="text-lime-600 font-bold">{template.target_mastery}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createPlan}
                  disabled={creating || !newPlan.category}
                  className="flex-1 py-3 bg-lime-500 text-white font-bold rounded-xl hover:bg-lime-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creating ? "Creating..." : (
                    <>Start Plan <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyPlans;
